import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { SalesforceSourceSettingsApi } from "./SalesforceSourceSettings.Api";
import type { SalesforceSourceSettingsViewModel } from "./SalesforceSourceSettings.ViewModel.types";
import type {
  SalesforceConfiguration,
  SalesforceFieldDefinition,
  SalesforceObjectDefinition,
  SalesforcePicklistValue,
} from "./SalesforceSourceSettings.types";

const NONE_OPTION = "__none";

const DEFAULT_STATUSES_TO_LOAD = [
  "not started",
  "in progress",
  "waiting on someone else",
  "new",
  "working",
  "escalated",
  "open - not contacted",
  "working - contacted",
];

const DEFAULT_DONE_STATUSES = [
  "completed",
  "done",
  "closed",
  "sent",
  "closed - converted",
];

const seedStatusesToLoad = (
  statuses: SalesforcePicklistValue[],
): SalesforcePicklistValue[] =>
  statuses.filter((s) => DEFAULT_STATUSES_TO_LOAD.includes(s.label.toLowerCase()));

const seedDoneStatus = (
  statuses: SalesforcePicklistValue[],
): SalesforcePicklistValue | null =>
  statuses.find((s) => DEFAULT_DONE_STATUSES.includes(s.label.toLowerCase())) ?? null;

/**
 * Keeps the saved statuses that the status field still offers, and seeds the defaults only
 * where nothing saved survives. Reseeding on every load wiped the user's choices the next
 * time any setting was saved.
 */
const keepSavedStatusesOrSeed = (
  saved: Partial<SalesforceConfiguration>,
  statusField: SalesforceFieldDefinition | null,
): Pick<SalesforceConfiguration, "statusesToLoad" | "doneStatus"> => {
  const statuses = statusField?.picklistValues ?? [];
  const isSameField = saved.statusField?.name === statusField?.name;
  const isOffered = (status: SalesforcePicklistValue): boolean =>
    statuses.some((s) => s.value === status.value);

  const keptStatusesToLoad = isSameField
    ? (saved.statusesToLoad ?? []).filter(isOffered)
    : [];
  const keptDoneStatus =
    isSameField && saved.doneStatus && isOffered(saved.doneStatus)
      ? saved.doneStatus
      : null;

  return {
    statusesToLoad:
      keptStatusesToLoad.length > 0 ? keptStatusesToLoad : seedStatusesToLoad(statuses),
    doneStatus: keptDoneStatus ?? seedDoneStatus(statuses),
  };
};

export const useSalesforceSourceSettingsViewModel = (
  model: SourceSettingsModel,
): SalesforceSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const objectDefinitionsRef = useRef<SalesforceObjectDefinition[]>([]);
  const picklistFieldsRef = useRef<SalesforceFieldDefinition[]>([]);
  const dateFieldsRef = useRef<SalesforceFieldDefinition[]>([]);

  const [objectDefinitions, setObjectDefinitions] = useState<SalesforceObjectDefinition[]>([]);
  const [objectDefinitionsLoaded, setObjectDefinitionsLoaded] = useState(false);
  const [picklistFields, setPicklistFields] = useState<SalesforceFieldDefinition[]>([]);
  const [dateFields, setDateFields] = useState<SalesforceFieldDefinition[]>([]);
  const [objectMetadataLoaded, setObjectMetadataLoaded] = useState(false);
  const [statuses, setStatuses] = useState<SalesforcePicklistValue[]>(() => {
    const initialConfig = parseConnectionConfiguration<SalesforceConfiguration>(
      model.editingConnection.configuration,
    );
    return initialConfig.statusField?.picklistValues ?? [];
  });
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.Salesforce && model.editingConnection.isAuthorised;
  const isAuthorisedProfileReady =
    isVisible && Boolean(model.editingConnection.profileName);

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<SalesforceConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  const selectedObjectDefinitionName = configuration.objectDefinition?.name ?? "";
  const selectedStatusFieldName = configuration.statusField?.name ?? "";

  const selectedStatusesToLoad = useMemo(
    () => configuration.statusesToLoad?.map((s) => s.value) ?? [],
    [configuration.statusesToLoad],
  );
  const selectedDoneStatusValue = configuration.doneStatus?.value ?? NONE_OPTION;
  const selectedDueDateFieldName = configuration.dueDateField?.name ?? NONE_OPTION;

  // Load object definitions when connection/authorisation changes.
  useEffect(() => {
    const loadObjectDefinitions = async (): Promise<void> => {
      if (!isAuthorisedProfileReady || !model.boardId || !editingConnectionId) {
        objectDefinitionsRef.current = [];
        setObjectDefinitions([]);
        setObjectDefinitionsLoaded(true);
        return;
      }

      const sfApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<SalesforceSourceSettingsApi>(
          SourceTypeIds.Salesforce,
        );
      if (!sfApi) {
        objectDefinitionsRef.current = [];
        setObjectDefinitions([]);
        setObjectDefinitionsLoaded(true);
        return;
      }

      setObjectDefinitionsLoaded(false);
      setHasError(false);
      try {
        const loaded = await sfApi.getObjectDefinitions(
          model.boardId,
          editingConnectionId,
        );
        objectDefinitionsRef.current = loaded;
        setObjectDefinitions(loaded);

        // Default to 'Task' if no object definition is set.
        const currentConfig = parseConnectionConfiguration<SalesforceConfiguration>(
          editingConnectionRef.current.configuration,
        );
        if (!currentConfig.objectDefinition) {
          const defaultObject = loaded.find((o) => o.name === "Task") ?? null;
          if (defaultObject) {
            updateConnectionConfiguration<SalesforceConfiguration>(
              editingConnectionRef.current,
              onUpdateRef.current,
              (cfg) =>
                cfg.objectDefinition
                  ? cfg
                  : { ...cfg, objectDefinition: defaultObject },
              { isDefault: true },
            );
          }
        }
      } catch {
        objectDefinitionsRef.current = [];
        setObjectDefinitions([]);
        setHasError(true);
      } finally {
        setObjectDefinitionsLoaded(true);
      }
    };

    void loadObjectDefinitions();
  }, [editingConnectionId, isAuthorisedProfileReady, model.boardId]); 
  // Load object metadata when the selected object definition changes.
  useEffect(() => {
    const loadMetadata = async (): Promise<void> => {
      if (
        !isAuthorisedProfileReady ||
        !model.boardId ||
        !editingConnectionId ||
        !selectedObjectDefinitionName
      ) {
        picklistFieldsRef.current = [];
        dateFieldsRef.current = [];
        setPicklistFields([]);
        setDateFields([]);
        setStatuses([]);
        setObjectMetadataLoaded(true);
        return;
      }

      const sfApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<SalesforceSourceSettingsApi>(
          SourceTypeIds.Salesforce,
        );
      if (!sfApi) {
        picklistFieldsRef.current = [];
        dateFieldsRef.current = [];
        setPicklistFields([]);
        setDateFields([]);
        setStatuses([]);
        setObjectMetadataLoaded(true);
        return;
      }

      setObjectMetadataLoaded(false);
      setHasError(false);
      try {
        const currentConfig = parseConnectionConfiguration<SalesforceConfiguration>(
          editingConnectionRef.current.configuration,
        );
        const objectDefinition =
          currentConfig.objectDefinition ??
          objectDefinitionsRef.current.find(
            (o) => o.name === selectedObjectDefinitionName,
          ) ??
          null;

        if (!objectDefinition) {
          picklistFieldsRef.current = [];
          dateFieldsRef.current = [];
          setPicklistFields([]);
          setDateFields([]);
          setStatuses([]);
          setObjectMetadataLoaded(true);
          return;
        }

        const metadata = await sfApi.getObjectDefinitionMetadata(
          model.boardId,
          editingConnectionId,
          objectDefinition,
        );

        picklistFieldsRef.current = metadata.picklistFields;
        dateFieldsRef.current = metadata.dateFields;
        setPicklistFields(metadata.picklistFields);
        setDateFields(metadata.dateFields);

        // Find effective status field — keep saved or default to 'Status'.
        const effectiveStatusField =
          (currentConfig.statusField
            ? metadata.picklistFields.find(
                (f) => f.name === currentConfig.statusField?.name,
              ) ?? null
            : null) ??
          metadata.picklistFields.find((f) => f.name === "Status") ??
          null;

        const effectiveStatuses = effectiveStatusField?.picklistValues ?? [];
        setStatuses(effectiveStatuses);

        // Find effective due date field — keep saved or default to 'ActivityDate'.
        const effectiveDueDateField =
          (currentConfig.dueDateField
            ? metadata.dateFields.find(
                (f) => f.name === currentConfig.dueDateField?.name,
              ) ?? null
            : null) ??
          metadata.dateFields.find((f) => f.name === "ActivityDate") ??
          null;

        updateConnectionConfiguration<SalesforceConfiguration>(
          editingConnectionRef.current,
          onUpdateRef.current,
          (cfg) => ({
            ...cfg,
            nameField: metadata.nameField,
            statusField: effectiveStatusField,
            dueDateField: effectiveDueDateField,
            ...keepSavedStatusesOrSeed(cfg, effectiveStatusField),
          }),
        );
      } catch {
        picklistFieldsRef.current = [];
        dateFieldsRef.current = [];
        setPicklistFields([]);
        setDateFields([]);
        setStatuses([]);
        setHasError(true);
      } finally {
        setObjectMetadataLoaded(true);
      }
    };

    void loadMetadata();
  }, [editingConnectionId, isAuthorisedProfileReady, model.boardId, selectedObjectDefinitionName]); 
  const onSelectObjectDefinition = (name?: string): void => {
    const objectDefinition =
      name && name !== NONE_OPTION
        ? (objectDefinitionsRef.current.find((o) => o.name === name) ?? null)
        : null;

    picklistFieldsRef.current = [];
    dateFieldsRef.current = [];
    setPicklistFields([]);
    setDateFields([]);
    setStatuses([]);

    updateConnectionConfiguration<SalesforceConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        objectDefinition,
        statusField: null,
        statusesToLoad: null,
        doneStatus: null,
        dueDateField: null,
        nameField: null,
      }),
    );
  };

  const onSelectStatusField = (name?: string): void => {
    const statusField =
      name && name !== NONE_OPTION
        ? (picklistFieldsRef.current.find((f) => f.name === name) ?? null)
        : null;

    const newStatuses = statusField?.picklistValues ?? [];
    setStatuses(newStatuses);

    const newStatusesToLoad = seedStatusesToLoad(newStatuses);
    const newDoneStatus = seedDoneStatus(newStatuses);

    updateConnectionConfiguration<SalesforceConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        statusField,
        statusesToLoad: newStatusesToLoad,
        doneStatus: newDoneStatus,
      }),
    );
  };

  const onSelectStatusesToLoad = (values: string[]): void => {
    const newStatusesToLoad = statuses.filter((s) => values.includes(s.value));
    updateConnectionConfiguration<SalesforceConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({ ...cfg, statusesToLoad: newStatusesToLoad }),
    );
  };

  const onSelectDoneStatus = (value?: string): void => {
    const doneStatus =
      value && value !== NONE_OPTION
        ? (statuses.find((s) => s.value === value) ?? null)
        : null;
    updateConnectionConfiguration<SalesforceConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({ ...cfg, doneStatus }),
    );
  };

  const onSelectDueDateField = (name?: string): void => {
    const dueDateField =
      name && name !== NONE_OPTION
        ? (dateFieldsRef.current.find((f) => f.name === name) ?? null)
        : null;
    updateConnectionConfiguration<SalesforceConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({ ...cfg, dueDateField }),
    );
  };

  return {
    isVisible,
    isAuthorisedProfileReady,
    objectDefinitions,
    objectDefinitionsLoaded,
    selectedObjectDefinitionName: selectedObjectDefinitionName || NONE_OPTION,
    picklistFields,
    dateFields,
    objectMetadataLoaded,
    statuses,
    selectedStatusFieldName: selectedStatusFieldName || NONE_OPTION,
    selectedStatusesToLoad,
    selectedDoneStatusValue,
    selectedDueDateFieldName,
    noneOptionValue: NONE_OPTION,
    hasError,
    onSelectObjectDefinition,
    onSelectStatusField,
    onSelectStatusesToLoad,
    onSelectDoneStatus,
    onSelectDueDateField,
  };
};
